import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, ButtonGroup, Spinner, Alert, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSyncAlt, faMagic, faHourglassHalf, faCalendarDay, faCalendarWeek, faCalendarAlt, faList, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery } from 'react-responsive';
import { usePageTitle } from '../context/PageTitleContext';
import { generateDailyCalendarSummary, generatePendingCalendarSummary, getCalendarEvents } from '../services/api';
import { toast } from 'react-toastify';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parseISO, startOfWeek, endOfWeek, getDay, addMonths, startOfMonth, endOfMonth, startOfDay, endOfDay, subMonths, subDays } from 'date-fns';
import itLocale from 'date-fns/locale/it';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/Calendar.css';

import EventDetailModal from '../components/EventDetailModal';

// Configura il localizzatore per react-big-calendar
const locales = {
  'it': itLocale,
};

const localizer = dateFnsLocalizer({
  format,
  parse: parseISO,
  startOfWeek,
  getDay,
  locales,
});

// Mappatura dei colori di Google Calendar
const G_CALENDAR_COLOR_MAP = {
    '1': '#a4bdfc', // Blu (original: Light Blue)
    '2': '#7ae7bf', // Verde smeraldo (original: Green)
    '3': '#dbadff', // Viola chiaro (original: Purple)
    '4': '#ff887c', // Rosso (original: Red)
    '5': '#fbd75b', // Giallo (original: Yellow)
    '6': '#ffb878', // Arancione (original: Orange)
    '7': '#46d6db', // Turchese (original: Turquoise)
    '8': '#e1e1e1', // Grigio (original: Gray)
    '9': '#5484ed', // Blu scuro (original: Bold Blue)
    '10': '#51b749', // Verde scuro (original: Bold Green)
    '11': '#dc2127', // Rosso vivo (original: Bold Red)
};

const CustomToolbar = (toolbar) => {
    const goToBack = () => toolbar.onNavigate('PREV');
    const goToNext = () => toolbar.onNavigate('NEXT');
    const goToCurrent = () => toolbar.onNavigate('TODAY');
    
    const isMobile = useMediaQuery({ maxWidth: 991 });
  
    const getViewIcon = (view) => {
        switch(view) {
            case 'day': return faCalendarDay;
            case 'week': return faCalendarWeek;
            case 'month': return faCalendarAlt;
            case 'agenda': return faList;
            default: return faCalendarAlt;
        }
    };

    const getViewLabel = (view) => {
        switch(view) {
            case 'day': return 'Giorno';
            case 'week': return 'Settimana'; 
            case 'month': return 'Mese';
            case 'agenda': return 'Lista';
            default: return 'Mese';
        }
    };
  
    return (
      <div className="rbc-toolbar">
        <ButtonGroup className="me-2">
          <Button 
            variant="outline-secondary" 
            onClick={goToBack}
            className={`${isMobile ? 'btn-sm' : ''}`}
          >
            {isMobile ? '‹' : 'Precedente'}
          </Button>
          <Button 
            variant="primary" 
            onClick={goToCurrent}
            className={`${isMobile ? 'btn-sm' : ''}`}
          >
            Oggi
          </Button>
          <Button 
            variant="outline-secondary" 
            onClick={goToNext}
            className={`${isMobile ? 'btn-sm' : ''}`}
          >
            {isMobile ? '›' : 'Successivo'}
          </Button>
        </ButtonGroup>
  
        <span className={`rbc-toolbar-label ${isMobile ? 'small' : ''}`} style={{ textTransform: 'capitalize' }}>
          {toolbar.label}
        </span>
  
        {isMobile ? (
          <Dropdown align="end">
            <Dropdown.Toggle variant="outline-secondary" id="view-dropdown" className="btn-sm">
              <FontAwesomeIcon icon={getViewIcon(toolbar.view)} />
              {' ' + getViewLabel(toolbar.view)}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => toolbar.onView('day')} active={toolbar.view === 'day'}>
                <FontAwesomeIcon icon={faCalendarDay} className="me-2" />
                Giorno
              </Dropdown.Item>
              <Dropdown.Item onClick={() => toolbar.onView('week')} active={toolbar.view === 'week'}>
                <FontAwesomeIcon icon={faCalendarWeek} className="me-2" />
                Settimana
              </Dropdown.Item>
              <Dropdown.Item onClick={() => toolbar.onView('month')} active={toolbar.view === 'month'}>
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                Mese
              </Dropdown.Item>
              <Dropdown.Item onClick={() => toolbar.onView('agenda')} active={toolbar.view === 'agenda'}>
                <FontAwesomeIcon icon={faList} className="me-2" />
                Lista
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          <ButtonGroup>
            <Button variant={toolbar.view === 'day' ? 'primary' : 'outline-secondary'} onClick={() => toolbar.onView('day')}>
              <FontAwesomeIcon icon={faCalendarDay} /> Giorno
            </Button>
            <Button variant={toolbar.view === 'week' ? 'primary' : 'outline-secondary'} onClick={() => toolbar.onView('week')}>
              <FontAwesomeIcon icon={faCalendarWeek} /> Settimana
            </Button>
            <Button variant={toolbar.view === 'month' ? 'primary' : 'outline-secondary'} onClick={() => toolbar.onView('month')}>
              <FontAwesomeIcon icon={faCalendarAlt} /> Mese
            </Button>
            <Button variant={toolbar.view === 'agenda' ? 'primary' : 'outline-secondary'} onClick={() => toolbar.onView('agenda')}>
              <FontAwesomeIcon icon={faList} /> Agenda
            </Button>
          </ButtonGroup>
        )}
      </div>
    );
};

const CalendarioPage = () => {
    usePageTitle('Calendario Appuntamenti');
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGeneratingPending, setIsGeneratingPending] = useState(false);
    const [view, setView] = useState('month');
    const [date, setDate] = useState(new Date());

    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    
    const isMobile = useMediaQuery({ maxWidth: 991 });

    const handleEventClose = () => setShowEventModal(false);
    const handleEventShow = (event) => {
        setSelectedEvent(event);
        setShowEventModal(true);
    };
    
    const eventPropGetter = useCallback(
        (event) => {
            const backgroundColor = G_CALENDAR_COLOR_MAP[event.resource.colorId] || '#6c757d';
            return {
                style: {
                    backgroundColor,
                    borderColor: backgroundColor,
                    color: '#fff',
                },
            };
        },
        []
    );

    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            let timeMin = startOfMonth(date);
            let timeMax = endOfMonth(date);

            if (view === 'week') {
                timeMin = startOfWeek(date, { locale: itLocale });
                timeMax = endOfWeek(date, { locale: itLocale });
            } else if (view === 'day') {
                timeMin = startOfDay(date);
                timeMax = endOfDay(date);
            } else if (view === 'agenda') {
                timeMin = subMonths(date, 1);
                timeMax = addMonths(date, 3);
            }
            
            const response = await getCalendarEvents(timeMin, timeMax);
            if (response.data) {
                const formattedEvents = response.data.map(event => {
                    let startDateTime, endDateTime;

                    if (event.start && event.start.dateTime) {
                        startDateTime = parseISO(event.start.dateTime);
                    } else if (event.start && event.start.date) {
                        startDateTime = parseISO(event.start.date);
                    } else {
                        startDateTime = new Date();
                    }

                    if (event.end && event.end.dateTime) {
                        endDateTime = parseISO(event.end.dateTime);
                    } else if (event.end && event.end.date) {
                        const parsedEndDate = parseISO(event.end.date);
                        if (event.start.date === event.end.date || format(startDateTime, 'yyyy-MM-dd') !== format(parsedEndDate, 'yyyy-MM-dd')) {
                            endDateTime = subDays(parsedEndDate, 1);
                        } else {
                            endDateTime = parsedEndDate;
                        }
                    } else {
                        endDateTime = new Date();
                    }
                    
                    return {
                        id: event.id,
                        title: event.summary,
                        start: startDateTime,
                        end: endDateTime,
                        allDay: (event.start && event.start.date && !event.start.dateTime),
                        resource: event,
                    };
                });
                setEvents(formattedEvents);
            }
        } catch (err) {
            console.error("Errore nel caricamento degli eventi del calendario:", err);
            setError('Impossibile caricare gli eventi del calendario. Controlla la console.');
        } finally {
            setLoading(false);
        }
    }, [date, view]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleRefresh = () => {
        fetchEvents();
        toast.info("Calendario aggiornato!");
    };

    const handleGenerateAgenda = async () => {
        setIsGenerating(true);
        try {
            const response = await generateDailyCalendarSummary();
            toast.success(response.data.message || "Agenda giornaliera creata con successo!");
            fetchEvents();
        } catch (error) {
            console.error("Errore durante la generazione dell'agenda giornaliera:", error);
            toast.error("Errore durante la generazione dell'agenda giornaliera. Controlla la console.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleGeneratePendingAgenda = async () => {
        setIsGeneratingPending(true);
        try {
            const response = await generatePendingCalendarSummary();
            toast.success(response.data.message || "Agenda ordini in sospeso creata con successo!");
            fetchEvents();
        } catch (error) {
            console.error("Errore durante la generazione dell'agenda ordini in sospeso:", error);
            toast.error("Errore durante la generazione dell'agenda ordini in sospeso. Controlla la console.");
        } finally {
            setIsGeneratingPending(false);
        }
    };

    const handleNavigate = useCallback((newDate) => setDate(newDate), [setDate]);
    const handleView = useCallback((newView) => setView(newView), [setView]);

    if (loading && events.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Caricamento Calendario...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div className="container-fluid">
            <Card className="shadow-sm">
                <Card.Header as="h5" className="d-flex flex-wrap justify-content-between align-items-center">
                    <div className="mb-2 mb-md-0">La tua Agenda</div>
                    {isMobile ? (
                        <div className="d-flex align-items-center">
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/calendario/nuovo')} 
                                className="me-2"
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                {' Nuovo'}
                            </Button>
                            <Dropdown align="end">
                                <Dropdown.Toggle variant="outline-secondary" size="sm" id="actions-dropdown">
                                    <FontAwesomeIcon icon={faEllipsisVertical} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleGenerateAgenda} disabled={isGenerating}>
                                        <FontAwesomeIcon icon={faMagic} className="me-2" />
                                        {isGenerating ? 'Generazione...' : 'Agenda da Ordini'}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleGeneratePendingAgenda} disabled={isGeneratingPending}>
                                        <FontAwesomeIcon icon={faHourglassHalf} className="me-2" />
                                        {isGeneratingPending ? 'Generazione...' : 'Agenda Sospesi'}
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={handleRefresh}>
                                        <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                                        Aggiorna
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    ) : (
                        <div className="d-flex flex-wrap justify-content-end align-items-center">
                            <Button variant="info" onClick={handleGenerateAgenda} className="me-2 mb-2 mb-md-0" disabled={isGenerating}>
                                <FontAwesomeIcon icon={faMagic} className="me-2" />
                                {isGenerating ? 'Generazione...' : 'Genera Agenda da Ordini'}
                            </Button>
                            <Button variant="warning" onClick={handleGeneratePendingAgenda} className="me-2 mb-2 mb-md-0" disabled={isGeneratingPending}>
                                <FontAwesomeIcon icon={faHourglassHalf} className="me-2" />
                                {isGeneratingPending ? 'Generazione...' : 'Genera Agenda Ordini in Sospeso'}
                            </Button>
                            <Button variant="outline-secondary" onClick={handleRefresh} className="me-2 mb-2 mb-md-0">
                                <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                                Aggiorna Calendario
                            </Button>
                            <Button variant="primary" onClick={() => navigate('/calendario/nuovo')} className="mb-2 mb-md-0">
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Nuovo Evento
                            </Button>
                        </div>
                    )}
                </Card.Header>
                <Card.Body style={{ height: isMobile ? '500px' : '700px', padding: 0 }}>
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center h-100">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Caricamento Calendario...</span>
                            </Spinner>
                        </div>
                    ) : (
                        <Calendar
                            localizer={localizer}
                            events={events}
                            view={view}
                            date={date}
                            onNavigate={handleNavigate}
                            onView={handleView}
                            onSelectEvent={handleEventShow}
                            eventPropGetter={eventPropGetter}
                            startAccessor="start"
                            endAccessor="end"
                            titleAccessor="title"
                            style={{ height: '100%' }}
                            messages={{
                                allDay: 'Tutto il giorno',
                                previous: 'Precedente',
                                next: 'Successivo',
                                today: 'Oggi',
                                month: 'Mese',
                                week: 'Settimana',
                                day: 'Giorno',
                                agenda: 'Agenda',
                                date: 'Data',
                                time: 'Ora',
                                event: 'Evento',
                                noEventsInRange: 'Nessun evento in questo intervallo.',
                                showMore: total => `+ ${total} altro/i`
                            }}
                            culture='it'
                            components={{ toolbar: CustomToolbar }}
                            popup={!isMobile} // Disabilita popup su mobile
                            step={isMobile ? 60 : 30} // Slot temporali più larghi su mobile
                            timeslots={isMobile ? 1 : 2} // Meno slot su mobile
                        />
                    )}
                </Card.Body>
            </Card>
            {selectedEvent && (
                <EventDetailModal
                    show={showEventModal}
                    handleClose={handleEventClose}
                    event={selectedEvent}
                />
            )}
        </div>
    );
};

export default CalendarioPage;
